import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import FAQAssist from '../FAQAssist';

const ForeignTravelTest: React.FC = () => {
  const [hasForeignTravel, setHasForeignTravel] = useState<string>('');
  const [clearanceLevel, setClearanceLevel] = useState<string>('');

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/rag-test" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to RAG Testing Dashboard
        </Link>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Foreign Travel Reporting Test</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Foreign Travel:</strong> Test the FAQ system for unofficial foreign travel reporting requirements,
                including itinerary submission, pre-travel approval, and deviation reporting.
              </p>
            </div>

            {/* Clearance Level Selector */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <label htmlFor="clearanceLevel" className="block text-sm font-medium text-green-900 mb-2">
                Clearance Level (Optional - filters requirements)
              </label>
              <select
                id="clearanceLevel"
                value={clearanceLevel}
                onChange={(e) => setClearanceLevel(e.target.value)}
                className="w-full md:w-64 px-3 py-2 border border-green-300 rounded-md text-sm focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Levels (No Filtering)</option>
                <option value="confidential">Confidential</option>
                <option value="secret">Secret</option>
                <option value="l">L (DOE)</option>
                <option value="top_secret">Top Secret</option>
                <option value="q">Q (DOE)</option>
                <option value="sci">SCI</option>
              </select>
              <p className="text-xs text-green-700 mt-2">
                SEAD-3 has different reporting requirements based on clearance level. Select a level to see tailored FAQs and assessments.
              </p>
            </div>
          </div>

          <div className="space-y-8">
            {/* Foreign Travel Question with FAQ */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex-1">
                  Have you traveled outside the United States for personal reasons in the past 7 years?
                </h3>
                <FAQAssist
                  topic="foreign-travel-reporting"
                  clearanceLevel={clearanceLevel || undefined}
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="hasForeignTravel"
                    value="yes"
                    checked={hasForeignTravel === 'yes'}
                    onChange={(e) => setHasForeignTravel(e.target.value)}
                    className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="hasForeignTravel"
                    value="no"
                    checked={hasForeignTravel === 'no'}
                    onChange={(e) => setHasForeignTravel(e.target.value)}
                    className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">No</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="hasForeignTravel"
                    value="unsure"
                    checked={hasForeignTravel === 'unsure'}
                    onChange={(e) => setHasForeignTravel(e.target.value)}
                    className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">I'm not sure if it needs to be reported</span>
                </label>
              </div>

              {/* Additional context for unsure selection */}
              {hasForeignTravel === 'unsure' && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-amber-900 mb-1">Need Help Determining Reporting Requirements?</h4>
                      <p className="text-sm text-amber-800">
                        Use the FAQ Assistant above to get guidance on what foreign travel requires reporting,
                        or describe your specific travel situation for a personalized assessment.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Example scenarios */}
            {hasForeignTravel === 'yes' && (
              <div className="p-6 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Common Travel Scenarios - Test with FAQ</h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Vacation to Mexico or Canada for a few days</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Travel to Puerto Rico or Guam (U.S. territories)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Business trip to Europe or Asia</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Unexpected border crossing during travel</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Emergency international travel</span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Sample Questions */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-3">Sample Questions for Testing</h4>
            <p className="text-xs text-blue-800 mb-3">Click the copy icon to copy a question, then paste it into the FAQ Assistant's personalized assessment field:</p>
            <div className="space-y-2">
              {[
                "I'm planning a vacation to Mexico for a week. Do I need to get pre-approval or submit an itinerary?",
                "I'm traveling to Germany for a conference next month. What are my reporting requirements?",
                "I had an emergency and needed to fly to Japan immediately. Do I still need to report this travel?",
                "During my vacation in France, I took a day trip to Switzerland that wasn't on my itinerary. Do I need to report this deviation?",
                "I'm visiting Puerto Rico next month. Is this considered foreign travel that needs to be reported?"
              ].map((q, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 px-3 py-2 text-sm text-gray-700 bg-white rounded border border-blue-200"
                >
                  <span className="flex-1">{q}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(q);
                      const btn = e.currentTarget;
                      const originalHTML = btn.innerHTML;
                      btn.innerHTML = '<svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>';
                      setTimeout(() => { btn.innerHTML = originalHTML; }, 1000);
                    }}
                    className="flex-shrink-0 p-1 hover:bg-blue-100 rounded transition-colors"
                    title="Copy to clipboard"
                  >
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Testing Instructions */}
          <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <h4 className="text-sm font-medium text-purple-900 mb-2">Testing Notes</h4>
            <ul className="text-sm text-purple-800 space-y-1">
              <li>• Test both pre-generated FAQs and custom situation assessment</li>
              <li>• Try questions about Mexico/Canada travel vs. other countries</li>
              <li>• Ask about pre-approval requirements and timing</li>
              <li>• Test edge cases like emergency travel or U.S. territory visits</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForeignTravelTest;
